/**
 * Schedule & shots (production plan v2): the day as a rundown of blocks in the shoot's local time,
 * plus a shot list. Blocks feed the live run sheet ("Next up" + 60 s / 10 s heads-ups); shots are
 * ticked off in Live and summarised in Replay.
 *
 * Times are 'HH:MM' wall-clock in the mission time zone on the block's day. A block without a start
 * follows the previous block's end (shown as an estimate). Planning intent only — nothing here is an
 * observation, a clearance or a feasibility check.
 */
import type { ProductionPlan } from './productionPlan.ts';
import { sunTimes } from './sun.ts';
import { zonedWallTimeToUtc } from './tz.ts';
import type { EpochMs, LatLon } from './types.ts';

export type ScheduleKind = 'call' | 'briefing' | 'travel' | 'setup' | 'flight' | 'filming' | 'interview' | 'break' | 'wrap' | 'other';
export type ShotType = 'air_to_air' | 'ground_to_air' | 'static' | 'onboard' | 'drone' | 'tracking' | 'interview' | 'broll' | 'other';
export type ShotPriority = 'must' | 'want' | 'bonus';
export type ShotStatus = 'planned' | 'got' | 'retake' | 'dropped';

export interface ScheduleItem {
  id: string;
  kind: ScheduleKind;
  title: string;
  /** 'HH:MM' local; null = follows the previous block. */
  start: string | null;
  durationMin: number | null;
  /** A mission place (location id), or free text in `place`. */
  placeId: string | null;
  place: string;
  /** Flights and travel: where from / to (free text, e.g. "KCVO", "Hotel"). */
  from: string;
  to: string;
  aircraftIds: string[];
  cameraIds: string[];
  /** Mission people (call sheet) ids. */
  peopleIds: string[];
  notes: string;
}

export interface ScheduleDay {
  id: string;
  /** 'YYYY-MM-DD' local; null until set. */
  date: string | null;
  label: string;
  notes: string;
  items: ScheduleItem[];
}

export interface ShotPlan {
  id: string;
  title: string;
  description: string;
  type: ShotType;
  priority: ShotPriority;
  status: ShotStatus;
  /** The schedule block it is planned in; null = not scheduled yet. */
  itemId: string | null;
  cameraIds: string[];
  peopleIds: string[];
  aircraftIds: string[];
  placeId: string | null;
  place: string;
  /** Lens / framing / move, e.g. "600 mm · tight 3/4 front · pan L→R". */
  framing: string;
  notes: string;
  /** When it was marked got (server time) and by whom (display name). */
  gotAt: EpochMs | null;
  gotBy: string | null;
}

export interface SchedulePlan {
  version: 2;
  days: ScheduleDay[];
  shots: ShotPlan[];
}

export interface SchedulePlanRecord {
  revision: number;
  updatedAt: EpochMs | null;
  plan: SchedulePlan;
}

export const SCHEDULE_LIMITS = { days: 60, itemsPerDay: 150, shots: 500 } as const;

export const SCHEDULE_KINDS: ScheduleKind[] = ['call', 'briefing', 'travel', 'setup', 'flight', 'filming', 'interview', 'break', 'wrap', 'other'];
export const SCHEDULE_KIND_LABEL: Record<ScheduleKind, string> = {
  call: 'Crew call', briefing: 'Briefing', travel: 'Travel', setup: 'Setup', flight: 'Flight', filming: 'Filming',
  interview: 'Interview', break: 'Break / meal', wrap: 'Wrap', other: 'Other',
};
/** Default length (minutes) when a block of this kind is added. */
export const SCHEDULE_KIND_DEFAULT_MIN: Record<ScheduleKind, number | null> = {
  call: 0, briefing: 30, travel: 30, setup: 45, flight: 60, filming: 60, interview: 45, break: 45, wrap: 0, other: 30,
};
export const SHOT_TYPES: ShotType[] = ['air_to_air', 'ground_to_air', 'static', 'onboard', 'drone', 'tracking', 'interview', 'broll', 'other'];
export const SHOT_TYPE_LABEL: Record<ShotType, string> = {
  air_to_air: 'Air-to-air', ground_to_air: 'Ground-to-air', static: 'Static / locked-off', onboard: 'Onboard / cockpit',
  drone: 'Drone', tracking: 'Tracking vehicle', interview: 'Interview', broll: 'B-roll / details', other: 'Other',
};
export const SHOT_PRIORITY_LABEL: Record<ShotPriority, string> = { must: 'Must have', want: 'Want', bonus: 'If time' };
export const SHOT_STATUS_LABEL: Record<ShotStatus, string> = { planned: 'To get', got: 'Got it', retake: 'Retake', dropped: 'Dropped' };

export const emptySchedulePlan = (): SchedulePlan => ({ version: 2, days: [], shots: [] });

const uid = (): string =>
  typeof globalThis.crypto?.randomUUID === 'function' ? globalThis.crypto.randomUUID() : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

/** Random id for plan days, blocks and shots (works outside secure contexts too). */
export const planId = uid;

export function newScheduleItem(kind: ScheduleKind, patch: Partial<ScheduleItem> = {}): ScheduleItem {
  return {
    id: uid(), kind, title: kind === 'other' ? '' : SCHEDULE_KIND_LABEL[kind], start: null, durationMin: SCHEDULE_KIND_DEFAULT_MIN[kind],
    placeId: null, place: '', from: '', to: '', aircraftIds: [], cameraIds: [], peopleIds: [], notes: '', ...patch,
  };
}

export function newShot(patch: Partial<ShotPlan> = {}): ShotPlan {
  return {
    id: uid(), title: '', description: '', type: 'other', priority: 'want', status: 'planned', itemId: null,
    cameraIds: [], peopleIds: [], aircraftIds: [], placeId: null, place: '', framing: '', notes: '', gotAt: null, gotBy: null, ...patch,
  };
}

export function newScheduleDay(patch: Partial<ScheduleDay> = {}): ScheduleDay {
  return { id: uid(), date: null, label: '', notes: '', items: [], ...patch };
}

// ───────────────────────────── time helpers ─────────────────────────────

/** 'HH:MM' → minutes after midnight, or null. Accepts '7:30', '0730', '19:05'. */
export function parseClock(text: string | null | undefined): number | null {
  if (!text) return null;
  const m = /^\s*(\d{1,2}):?(\d{2})\s*$/.exec(text);
  if (!m) return null;
  const h = Number(m[1]), min = Number(m[2]);
  return h < 24 && min < 60 ? h * 60 + min : null;
}

/** minutes after midnight (may exceed 1440 for after midnight) → 'HH:MM'. */
export function formatClock(min: number): string {
  const m = ((Math.round(min) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

export function formatPlanMinutes(min: number | null): string {
  if (min === null) return '';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60), r = min % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
}

/** Epoch ms for a local day + minutes after midnight (minutes past 1440 roll into the next day). */
export function localMinutesToUtc(date: string, minutes: number, timezone: string): EpochMs {
  const [y, mo, d] = date.split('-').map(Number) as [number, number, number];
  const dayOffset = Math.floor(minutes / 1440);
  const base = new Date(Date.UTC(y, mo - 1, d + dayOffset));
  const within = minutes - dayOffset * 1440;
  return zonedWallTimeToUtc({ year: base.getUTCFullYear(), month: base.getUTCMonth() + 1, day: base.getUTCDate(), hour: Math.floor(within / 60), minute: within % 60 }, timezone);
}

// ───────────────────────────── resolving the rundown ─────────────────────────────

export interface ResolvedItem {
  item: ScheduleItem;
  index: number;
  /** Minutes after local midnight of the day (≥ 1440 after midnight); null when unknown. */
  startMin: number | null;
  endMin: number | null;
  /** true when the start follows the previous block rather than a typed time. */
  estimated: boolean;
}

/**
 * Walks the day in order: a typed start wins; otherwise a block starts when the previous one ends.
 * A typed time more than 6 h earlier than the running clock is read as after midnight (night shoots).
 */
export function resolveDay(day: ScheduleDay): ResolvedItem[] {
  let cursor: number | null = null;
  let lastStart: number | null = null;
  return day.items.map((item, index) => {
    let start = parseClock(item.start);
    let estimated = false;
    if (start !== null) {
      const ref = cursor ?? lastStart;
      while (ref !== null && start < ref - 360) start += 1440;
    } else if (cursor !== null) {
      start = cursor;
      estimated = true;
    }
    const end = start !== null && item.durationMin !== null ? start + item.durationMin : null;
    cursor = end ?? (start !== null ? null : cursor);
    if (start !== null) lastStart = start;
    return { item, index, startMin: start, endMin: end, estimated };
  });
}

/**
 * Keeps the rundown in time order after a typed time changes. Blocks without a start stay attached
 * behind the block they follow; blocks with nothing known keep their place.
 */
export function sortDayItems(day: ScheduleDay): ScheduleItem[] {
  const resolved = resolveDay(day);
  const keyed = resolved.map((r, i) => ({ r, key: r.startMin ?? (i > 0 ? null : -1), i }));
  // carry the previous key forward so untimed blocks travel with their predecessor
  let carry = -1;
  for (const k of keyed) {
    if (k.key === null) k.key = carry;
    else carry = k.key;
  }
  return [...keyed].sort((a, b) => a.key! - b.key! || a.i - b.i).map((k) => k.r.item);
}

// ───────────────────────────── analysis ─────────────────────────────

export interface ScheduleIssue {
  level: 'conflict' | 'warning' | 'info';
  message: string;
  dayId: string | null;
  itemId?: string;
  shotId?: string;
}

export interface ScheduleNames {
  aircraft: Map<string, string>;
  cameras: Map<string, string>;
  people: Map<string, string>;
  places: Map<string, string>;
}

export interface SunMarks {
  sunrise: number | null;
  goldenMorningEnd: number | null;
  goldenEveningStart: number | null;
  sunset: number | null;
  civilDusk: number | null;
}

export interface DaySummary {
  day: ScheduleDay;
  items: ResolvedItem[];
  startMin: number | null;
  endMin: number | null;
  sun: SunMarks | null;
}

export interface CallTime {
  personId: string;
  name: string;
  dayId: string;
  /** Earliest block this person is on. */
  callMin: number;
  place: string;
  itemTitle: string;
}

export interface ScheduleAnalysis {
  days: DaySummary[];
  issues: ScheduleIssue[];
  callTimes: CallTime[];
  shotCounts: { total: number; got: number; must: number; mustGot: number; unscheduled: number };
}

export function itemPlaceText(item: Pick<ScheduleItem, 'placeId' | 'place' | 'from' | 'to'>, places: Map<string, string>): string {
  if (item.from || item.to) return `${item.from || '?'} → ${item.to || '?'}`;
  return (item.placeId && places.get(item.placeId)) || item.place;
}

export function itemLabel(item: ScheduleItem): string {
  return item.title.trim() || SCHEDULE_KIND_LABEL[item.kind];
}

/** Sunrise, golden hours, sunset and dusk as minutes after local midnight on `date`. */
export function daySunMarks(date: string, center: LatLon, timezone: string): SunMarks | null {
  try {
    const noon = localMinutesToUtc(date, 12 * 60, timezone);
    const s = sunTimes(noon, center.lat, center.lon, timezone);
    const dayStart = localMinutesToUtc(date, 0, timezone);
    const m = (t: EpochMs | null) => (t === null ? null : Math.round((t - dayStart) / 60000));
    return { sunrise: m(s.sunrise), goldenMorningEnd: m(s.goldenHourMorningEnd), goldenEveningStart: m(s.goldenHourEveningStart), sunset: m(s.sunset), civilDusk: m(s.civilDusk) };
  } catch {
    return null;
  }
}

const FILMING_KINDS = new Set<ScheduleKind>(['flight', 'filming', 'interview']);

export function analyzeSchedule(plan: SchedulePlan, names: ScheduleNames, opts: { center?: LatLon | null; timezone?: string } = {}): ScheduleAnalysis {
  const issues: ScheduleIssue[] = [];
  const callTimes: CallTime[] = [];
  const days: DaySummary[] = plan.days.map((day) => {
    const items = resolveDay(day);
    const known = items.filter((r) => r.startMin !== null);
    const ends = items.map((r) => r.endMin ?? r.startMin).filter((v): v is number => v !== null);
    const sun = day.date && opts.center && opts.timezone ? daySunMarks(day.date, opts.center, opts.timezone) : null;
    return { day, items, startMin: known.length ? known[0]!.startMin : null, endMin: ends.length ? Math.max(...ends) : null, sun };
  });
  const dates = new Map<string, string>();
  for (const d of plan.days) {
    if (d.date && dates.has(d.date)) issues.push({ level: 'warning', dayId: d.id, message: `Two days share ${d.date}` });
    if (d.date) dates.set(d.date, d.id);
  }
  for (const summary of days) {
    const { day, items, sun } = summary;
    if (!day.date && items.length) issues.push({ level: 'info', dayId: day.id, message: `${dayName(plan, day)}: set a date so the schedule reaches Live` });
    // same camera / person / aircraft in two overlapping blocks
    const timed = items.filter((r) => r.startMin !== null && r.endMin !== null && r.endMin > r.startMin);
    for (let i = 0; i < timed.length; i++) {
      for (let j = i + 1; j < timed.length; j++) {
        const a = timed[i]!, b = timed[j]!;
        if (!(a.startMin! < b.endMin! && b.startMin! < a.endMin!)) continue;
        const clash = [
          ...a.item.cameraIds.filter((x) => b.item.cameraIds.includes(x)).map((x) => names.cameras.get(x) ?? 'A camera'),
          ...a.item.peopleIds.filter((x) => b.item.peopleIds.includes(x)).map((x) => names.people.get(x) ?? 'Someone'),
          ...a.item.aircraftIds.filter((x) => b.item.aircraftIds.includes(x)).map((x) => names.aircraft.get(x) ?? 'An aircraft'),
        ];
        if (clash.length)
          issues.push({ level: 'conflict', dayId: day.id, itemId: b.item.id, message: `${clash.join(', ')} double-booked: ${itemLabel(a.item)} (${formatClock(a.startMin!)}) and ${itemLabel(b.item)} (${formatClock(b.startMin!)})` });
      }
    }
    for (let i = 1; i < items.length; i++) {
      const prev = items[i - 1]!, cur = items[i]!;
      if (prev.endMin !== null && cur.startMin !== null && !cur.estimated && cur.startMin < prev.endMin)
        issues.push({ level: 'warning', dayId: day.id, itemId: cur.item.id, message: `${itemLabel(cur.item)} starts ${prev.endMin - cur.startMin} min before ${itemLabel(prev.item)} ends` });
    }
    for (const r of items) {
      if (r.item.kind === 'flight' && !r.item.aircraftIds.length && names.aircraft.size)
        issues.push({ level: 'info', dayId: day.id, itemId: r.item.id, message: `${itemLabel(r.item)}: choose the aircraft` });
      if (sun && FILMING_KINDS.has(r.item.kind) && r.endMin !== null && sun.civilDusk !== null && r.endMin > sun.civilDusk && (r.startMin ?? 0) < sun.civilDusk + 60)
        issues.push({ level: 'warning', dayId: day.id, itemId: r.item.id, message: `${itemLabel(r.item)} runs ${r.endMin - sun.civilDusk} min past dusk (${formatClock(sun.civilDusk)})` });
      if (sun && FILMING_KINDS.has(r.item.kind) && r.startMin !== null && sun.sunrise !== null && r.startMin < sun.sunrise - 30 && r.startMin > sun.sunrise - 240)
        issues.push({ level: 'info', dayId: day.id, itemId: r.item.id, message: `${itemLabel(r.item)} starts before sunrise (${formatClock(sun.sunrise)})` });
    }
    // call times: earliest block each person is on
    const firstFor = new Map<string, ResolvedItem>();
    for (const r of items) {
      if (r.startMin === null) continue;
      // a crew call with nobody picked is everyone's call
      const who = r.item.kind === 'call' && !r.item.peopleIds.length ? [...names.people.keys()] : r.item.peopleIds;
      for (const pid of who) if (!firstFor.has(pid)) firstFor.set(pid, r);
    }
    for (const [personId, r] of firstFor) {
      callTimes.push({ personId, name: names.people.get(personId) ?? 'Crew', dayId: day.id, callMin: r.startMin!, place: itemPlaceText(r.item, names.places), itemTitle: itemLabel(r.item) });
    }
  }
  // shots
  const itemIds = new Map(plan.days.flatMap((d) => d.items.map((i) => [i.id, { day: d, item: i }] as const)));
  let got = 0, must = 0, mustGot = 0, unscheduled = 0;
  for (const shot of plan.shots) {
    if (shot.status === 'got') got++;
    if (shot.priority === 'must' && shot.status !== 'dropped') {
      must++;
      if (shot.status === 'got') mustGot++;
    }
    const block = shot.itemId ? itemIds.get(shot.itemId) : undefined;
    if (!block) {
      if (shot.status === 'planned' || shot.status === 'retake') unscheduled++;
      if (shot.priority === 'must' && shot.status === 'planned')
        issues.push({ level: 'warning', dayId: null, shotId: shot.id, message: `Must-have shot "${shot.title || 'Untitled'}" isn't scheduled` });
      continue;
    }
    const missing = shot.cameraIds.filter((c) => block.item.cameraIds.length && !block.item.cameraIds.includes(c));
    if (missing.length)
      issues.push({ level: 'info', dayId: block.day.id, shotId: shot.id, itemId: block.item.id, message: `"${shot.title || 'Shot'}" uses ${missing.map((c) => names.cameras.get(c) ?? 'a camera').join(', ')}, which isn't on ${itemLabel(block.item)}` });
  }
  callTimes.sort((a, b) => a.callMin - b.callMin);
  return { days, issues, callTimes, shotCounts: { total: plan.shots.length, got, must, mustGot, unscheduled } };
}

function dayName(plan: SchedulePlan, day: ScheduleDay): string {
  return day.label || `Day ${plan.days.indexOf(day) + 1}`;
}

export function dayTitle(plan: Pick<SchedulePlan, 'days'>, day: ScheduleDay, timezone = 'UTC'): string {
  const n = plan.days.indexOf(day) + 1;
  if (!day.date) return day.label || `Day ${n}`;
  const t = localMinutesToUtc(day.date, 12 * 60, timezone);
  const d = new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short', month: 'short', day: 'numeric' }).format(t);
  return day.label ? `${day.label} · ${d}` : `Day ${n} · ${d}`;
}

// ───────────────────────────── live run sheet ─────────────────────────────

export interface PlannedRunSheetItem {
  itemId: string;
  order: number;
  label: string;
  scheduledT: EpochMs | null;
  durationSec: number | null;
  notes: string;
}

/** The schedule as run sheet rows for Live: every block in day order, timed when its day has a date. */
export function scheduleToRunSheet(plan: SchedulePlan, timezone: string, places: Map<string, string>): PlannedRunSheetItem[] {
  const out: PlannedRunSheetItem[] = [];
  for (const day of plan.days) {
    for (const r of resolveDay(day)) {
      const place = itemPlaceText(r.item, places);
      const shots = plan.shots.filter((s) => s.itemId === r.item.id && s.status !== 'dropped').length;
      out.push({
        itemId: r.item.id,
        order: out.length + 1,
        label: itemLabel(r.item).slice(0, 120),
        scheduledT: day.date && r.startMin !== null ? localMinutesToUtc(day.date, r.startMin, timezone) : null,
        durationSec: r.item.durationMin === null ? null : r.item.durationMin * 60,
        notes: [place, shots ? `${shots} shot${shots === 1 ? '' : 's'}` : '', r.item.notes].filter(Boolean).join(' · ').slice(0, 2000),
      });
    }
  }
  return out;
}

// ───────────────────────────── v1 → v2 ─────────────────────────────

/** Converts the earlier flight/route/leg planner document; nothing the producer entered is dropped. */
export function upgradeProductionPlan(v1: ProductionPlan, timezone: string): SchedulePlan {
  const resources = new Map(v1.resources.map((r) => [r.id, r]));
  const peopleOf = (ids: (string | null)[]) => [...new Set(ids.flatMap((id) => {
    const r = id ? resources.get(id) : undefined;
    if (!r) return [];
    if (r.personId) return [r.personId];
    return r.members.map((m) => resources.get(m)?.personId).filter((p): p is string => !!p);
  }))];
  const resourceNotes = (ids: (string | null)[]) => ids.map((id) => (id ? resources.get(id) : undefined)).filter((r) => r && !r.personId).map((r) => r!.name);
  const plan = emptySchedulePlan();
  for (const d of v1.days) {
    const day = newScheduleDay({ id: d.id, date: d.date, label: /^Day \d+$/.test(d.name) ? '' : d.name, notes: d.overnight ? `Overnight: ${d.overnight}` : '' });
    for (const f of d.flights) {
      const route = f.routes.find((r) => r.id === f.activeRouteId) ?? f.routes[0];
      let start: string | null = null;
      if (f.startUtc !== null) {
        const parts = new Intl.DateTimeFormat('en-GB', { timeZone: timezone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(f.startUtc);
        start = parts;
      }
      const legs = route?.legs.length ? route.legs : [{ id: `${f.id}-leg`, from: '', to: '', durationMin: null, assignments: [] }];
      legs.forEach((leg, li) => {
        const otherRoutes = li === 0 ? f.routes.filter((r) => r !== route).map((r) => `Alternative ${r.name}: ${r.legs.map((l) => `${l.from}→${l.to}`).join(', ')}`) : [];
        const item = newScheduleItem('flight', {
          id: leg.id,
          title: legs.length > 1 ? `${f.name} · leg ${li + 1}` : f.name,
          start: li === 0 ? start : null,
          durationMin: leg.durationMin,
          from: leg.from,
          to: leg.to,
          aircraftIds: f.aircraftId ? [f.aircraftId] : [],
          cameraIds: [...new Set(leg.assignments.map((a) => a.cameraId).filter((c): c is string => !!c))],
          peopleIds: peopleOf(leg.assignments.flatMap((a) => [...a.resourceIds, a.operatorId, a.driverId])),
          notes: [li === 0 ? f.briefing : '', ...otherRoutes].filter(Boolean).join('\n'),
        });
        day.items.push(item);
        for (const a of leg.assignments) {
          plan.shots.push(newShot({
            id: a.id,
            title: a.shot,
            type: a.platform === 'airborne' ? 'air_to_air' : a.platform === 'moving_car' ? 'tracking' : 'ground_to_air',
            itemId: item.id,
            cameraIds: a.cameraId ? [a.cameraId] : [],
            peopleIds: peopleOf([a.operatorId, a.driverId, ...a.resourceIds]),
            place: a.location,
            notes: [a.destination ? `Then to ${a.destination}` : '', ...resourceNotes([a.transportId, ...a.resourceIds]).map((n) => `Uses ${n}`)].filter(Boolean).join('\n'),
          }));
        }
      });
    }
    for (const m of d.overnightMoves ?? []) {
      day.items.push(newScheduleItem('travel', {
        id: m.id, title: 'Move to next location', from: m.from, to: m.to, durationMin: m.travelMin,
        peopleIds: peopleOf([...m.resourceIds, m.driverId]), notes: resourceNotes([...m.resourceIds, m.transportId]).join(', '),
      }));
    }
    plan.days.push(day);
  }
  return plan;
}

/** Reads any stored planner document as v2. */
export function asSchedulePlan(doc: unknown, timezone: string): SchedulePlan {
  if (doc && typeof doc === 'object' && (doc as { version?: unknown }).version === 2) return doc as SchedulePlan;
  if (doc && typeof doc === 'object' && (doc as { version?: unknown }).version === 1) return upgradeProductionPlan(doc as ProductionPlan, timezone);
  return emptySchedulePlan();
}

// ───────────────────────────── templates ─────────────────────────────

export type ScheduleTemplateId = 'air_to_air' | 'ground_to_air' | 'airport_ops' | 'interview_broll' | 'drone' | 'multi_day';

export interface ScheduleTemplate {
  id: ScheduleTemplateId;
  name: string;
  summary: string;
}

export const SCHEDULE_TEMPLATES: ScheduleTemplate[] = [
  { id: 'air_to_air', name: 'Air-to-air', summary: 'Brief, camera ship + subject flight, debrief' },
  { id: 'ground_to_air', name: 'Ground-to-air passes', summary: 'Camera positions on the ground, repeated passes' },
  { id: 'airport_ops', name: 'Airport / static day', summary: 'Hangar, walkaround, taxi and takeoff coverage' },
  { id: 'interview_broll', name: 'Interviews + B-roll', summary: 'Sit-downs, details and cutaways — no flying needed' },
  { id: 'drone', name: 'Drone shoot', summary: 'Site check, batteries, flights in the light' },
  { id: 'multi_day', name: 'Multi-day travel shoot', summary: 'Several days with travel between locations' },
];

/** A starting rundown and shot list for a shoot type. Times are a suggested shape — edit freely. */
export function scheduleFromTemplate(id: ScheduleTemplateId, opts: { date: string | null; aircraftIds?: string[]; cameraIds?: string[]; sun?: SunMarks | null }): SchedulePlan {
  const sun = opts.sun ?? null;
  /** A time near a light event, rounded to 5 min; fallback when the light is unknown. */
  const near = (min: number | null | undefined, offset: number, fallback: string) => (min == null ? fallback : formatClock(Math.round((min + offset) / 5) * 5));
  const ac = opts.aircraftIds ?? [];
  const cams = opts.cameraIds ?? [];
  const plan = emptySchedulePlan();
  const day = newScheduleDay({ date: opts.date });
  plan.days.push(day);
  const add = (kind: ScheduleKind, title: string, start: string | null, durationMin: number | null, patch: Partial<ScheduleItem> = {}) => {
    const item = newScheduleItem(kind, { title, start, durationMin, ...patch });
    day.items.push(item);
    return item;
  };
  const shot = (title: string, type: ShotType, priority: ShotPriority, item: ScheduleItem | null, patch: Partial<ShotPlan> = {}) =>
    plan.shots.push(newShot({ title, type, priority, itemId: item?.id ?? null, ...patch }));
  switch (id) {
    case 'air_to_air': {
      add('call', 'Crew call', '07:00', 0);
      add('briefing', 'Air-to-air brief (both crews)', '07:15', 45, { notes: 'Formation plan, positions, comms, breakaway words, cards.' });
      add('setup', 'Camera ship rig + checks', null, 45, { cameraIds: cams });
      const f = add('flight', 'Air-to-air flight', null, 75, { aircraftIds: ac, cameraIds: cams });
      add('break', 'Turnaround + footage check', null, 45);
      const f2 = add('flight', 'Air-to-air flight 2 (golden hour)', near(sun?.goldenEveningStart, -15, '17:30'), 60, { aircraftIds: ac, cameraIds: cams });
      add('briefing', 'Debrief', null, 20);
      add('wrap', 'Wrap', null, 0);
      shot('Formation join-up', 'air_to_air', 'must', f);
      shot('Side profile, matched speed', 'air_to_air', 'must', f, { framing: 'Full aircraft, level, 3/4 to profile' });
      shot('Front 3/4 hero', 'air_to_air', 'must', f);
      shot('Banking breakaway', 'air_to_air', 'want', f);
      shot('Golden-hour silhouette', 'air_to_air', 'want', f2);
      shot('Cockpit POV', 'onboard', 'bonus', f);
      break;
    }
    case 'ground_to_air': {
      add('call', 'Crew call', near(sun?.sunrise, -60, '06:30'), 0);
      add('briefing', 'Safety + positions brief', null, 20);
      add('travel', 'Drive to camera positions', null, 30);
      add('setup', 'Set up camera positions', null, 45, { cameraIds: cams });
      const p = add('flight', 'Passes', null, 90, { aircraftIds: ac, cameraIds: cams });
      add('break', 'Lunch', null, 45);
      const p2 = add('flight', 'Passes — evening light', near(sun?.goldenEveningStart, -30, '16:30'), 90, { aircraftIds: ac, cameraIds: cams });
      add('wrap', 'Wrap + data backup', null, 30);
      shot('Low flyby, long lens', 'ground_to_air', 'must', p, { framing: 'Long lens, pan with it' });
      shot('Takeoff', 'ground_to_air', 'must', p);
      shot('Landing', 'ground_to_air', 'must', p2);
      shot('Wide establishing with aircraft small in frame', 'static', 'want', p);
      shot('Overhead pass', 'ground_to_air', 'want', p2);
      break;
    }
    case 'airport_ops': {
      add('call', 'Crew call', '08:00', 0);
      add('setup', 'Hangar setup + lighting', '08:15', 60, { cameraIds: cams });
      const h = add('filming', 'Hangar / static aircraft', null, 90, { aircraftIds: ac, cameraIds: cams });
      add('break', 'Lunch', null, 45);
      const w = add('filming', 'Walkaround + preflight', null, 45, { aircraftIds: ac });
      const t = add('flight', 'Taxi, takeoff and landing coverage', null, 60, { aircraftIds: ac, cameraIds: cams });
      add('wrap', 'Wrap', null, 0);
      shot('Hangar doors open reveal', 'static', 'must', h);
      shot('Detail macros: livery, props, instruments', 'broll', 'want', h);
      shot('Pilot walkaround', 'broll', 'must', w);
      shot('Engine start', 'onboard', 'want', t);
      shot('Taxi past camera', 'ground_to_air', 'must', t);
      shot('Takeoff', 'ground_to_air', 'must', t);
      break;
    }
    case 'interview_broll': {
      add('call', 'Crew call', '08:00', 0);
      add('setup', 'Interview setup (lights, sound)', '08:15', 60, { cameraIds: cams });
      const i1 = add('interview', 'Interview 1', null, 45, { cameraIds: cams });
      const i2 = add('interview', 'Interview 2', null, 45, { cameraIds: cams });
      add('break', 'Lunch', null, 45);
      const b = add('filming', 'B-roll', null, 120, { cameraIds: cams });
      add('wrap', 'Wrap + release forms', null, 15);
      shot('Interview 1 — wide + close', 'interview', 'must', i1);
      shot('Interview 2 — wide + close', 'interview', 'must', i2);
      shot('Room tone (30 s)', 'other', 'must', i1);
      shot('Hands at work, details', 'broll', 'want', b);
      shot('Walk-and-talk', 'broll', 'want', b);
      shot('Establishing exterior', 'broll', 'must', b);
      break;
    }
    case 'drone': {
      add('call', 'Crew call', near(sun?.sunrise, -75, '06:00'), 0);
      add('briefing', 'Site check + airspace notes', null, 30);
      add('setup', 'Drone setup + batteries', null, 20, { cameraIds: cams });
      const f1 = add('filming', 'Drone flights — morning light', null, 60, { cameraIds: cams });
      add('break', 'Batteries + data', null, 30);
      const f2 = add('filming', 'Drone flights — follow shots', null, 60, { aircraftIds: ac, cameraIds: cams });
      add('wrap', 'Wrap', null, 0);
      shot('Rising reveal', 'drone', 'must', f1);
      shot('Top-down', 'drone', 'want', f1);
      shot('Follow / chase', 'drone', 'must', f2);
      shot('Orbit', 'drone', 'want', f2);
      break;
    }
    case 'multi_day': {
      day.label = 'Arrive + scout';
      add('travel', 'Travel to location', '09:00', 180);
      add('filming', 'Location scout', null, 120);
      add('briefing', 'Production meeting', null, 30);
      const d2 = newScheduleDay({ date: opts.date ? nextDate(opts.date, 1) : null, label: 'Shoot day' });
      plan.days.push(d2);
      d2.items.push(newScheduleItem('call', { title: 'Crew call', start: '06:30', durationMin: 0 }));
      const f = newScheduleItem('flight', { title: 'Flight', start: '07:30', durationMin: 90, aircraftIds: ac, cameraIds: cams });
      d2.items.push(f, newScheduleItem('break', { title: 'Lunch', durationMin: 45 }), newScheduleItem('filming', { title: 'Ground coverage', durationMin: 120, cameraIds: cams }), newScheduleItem('wrap', { title: 'Wrap', durationMin: 0 }));
      const d3 = newScheduleDay({ date: opts.date ? nextDate(opts.date, 2) : null, label: 'Travel + pickups' });
      plan.days.push(d3);
      const p = newScheduleItem('filming', { title: 'Pickups', start: '08:00', durationMin: 120, cameraIds: cams });
      d3.items.push(p, newScheduleItem('travel', { title: 'Travel home', durationMin: 180 }));
      shot('Arrival / place-setting', 'broll', 'want', day.items[0]!);
      shot('Hero flight', 'ground_to_air', 'must', f);
      shot('Pickups from the shot list', 'other', 'want', p);
      break;
    }
  }
  return plan;
}

export function nextDate(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

// ───────────────────────────── paste / shift ─────────────────────────────

const KIND_WORDS: Array<[RegExp, ScheduleKind]> = [
  [/\b(crew call|call time|call)\b/i, 'call'],
  [/\b(wrap|wrapped|strike)\b/i, 'wrap'],
  [/\b(brief|briefing|debrief|safety|meeting)\b/i, 'briefing'],
  [/\b(lunch|breakfast|dinner|meal|break|turnaround|refuel)\b/i, 'break'],
  [/\b(drive|travel|transit|transfer|reposition|move to|depart for|ferry)\b/i, 'travel'],
  [/\b(flight|fly|flying|takeoff|take-off|launch|sortie|passes|air-to-air|a2a|landing)\b/i, 'flight'],
  [/\b(set ?up|rig|rigging|load in|load-in|prep)\b/i, 'setup'],
  [/\b(interview|sit-down|sitdown)\b/i, 'interview'],
  [/\b(shoot|film|filming|b-roll|broll|coverage|beauty|scene)\b/i, 'filming'],
];

/** Best guess at a block type from its words; 'other' when nothing matches. */
export function guessKind(text: string): ScheduleKind {
  for (const [re, kind] of KIND_WORDS) if (re.test(text)) return kind;
  return 'other';
}

function clockFrom(text: string): number | null {
  const m = /^(\d{1,2})(?::?(\d{2}))?\s*(am|pm|a|p)?$/i.exec(text.trim());
  if (!m) return null;
  let h = Number(m[1]);
  const min = m[2] ? Number(m[2]) : 0;
  const ap = m[3]?.toLowerCase();
  if (!m[2] && !ap) return null; // a bare number is not a time
  if (ap?.startsWith('p') && h < 12) h += 12;
  if (ap?.startsWith('a') && h === 12) h = 0;
  return h < 24 && min < 60 ? h * 60 + min : null;
}

/**
 * Blocks from pasted text, one per line: "07:00 Crew call", "7:30am–8:15 Brief @ Hangar 2",
 * "0900-1030 Air-to-air flight", "Lunch (45 min)". Lines without a time follow the previous block.
 */
export function parsePastedSchedule(text: string): ScheduleItem[] {
  const out: ScheduleItem[] = [];
  for (const raw of text.split(/\r?\n/)) {
    let line = raw.trim().replace(/^\d{1,2}[.)]\s+(?=\D|\d{1,2}[:.]?\d{2})/, '').replace(/^[•*\-–—]\s*/, '').replace(/\t+/g, ' ').trim();
    if (!line) continue;
    let start: number | null = null, end: number | null = null;
    const tm = /^(\d{1,2}(?::?\d{2})?\s*(?:am|pm|a|p)?)(?:\s*(?:-|–|—|to)\s*(\d{1,2}(?::?\d{2})?\s*(?:am|pm|a|p)?))?(?:\s+|\s*[-–—:|,]\s*)(.+)$/i.exec(line);
    if (tm) {
      const s = clockFrom(tm[1]!);
      if (s !== null) {
        start = s;
        end = tm[2] ? clockFrom(tm[2]) : null;
        if (end !== null && end < start) end += 1440;
        line = tm[3]!.trim();
      }
    }
    let duration: number | null = end !== null && start !== null ? end - start : null;
    const dm = /\(?\b(\d+(?:\.\d+)?)\s*(h|hr|hrs|hours?|m|min|mins|minutes?)\b\)?/i.exec(line);
    if (dm && duration === null) {
      duration = Math.round(Number(dm[1]) * (dm[2]!.toLowerCase().startsWith('h') ? 60 : 1));
      line = line.replace(dm[0], '').replace(/\(\s*\)/, '').trim();
    }
    let place = '';
    const at = /\s+(?:@|at)\s+(.+)$/i.exec(line);
    if (at && line.indexOf(at[0]) > 0) {
      place = at[1]!.trim();
      line = line.slice(0, line.indexOf(at[0])).trim();
    }
    const title = line.replace(/[-–—:|,\s]+$/, '').slice(0, 200);
    if (!title) continue;
    const kind = guessKind(title);
    const item = newScheduleItem(kind, { title, start: start === null ? null : formatClock(start), durationMin: duration ?? SCHEDULE_KIND_DEFAULT_MIN[kind], place: place.slice(0, 200) });
    // "KCVO to KSLE", "KCVO → KSLE", "KCVO-KSLE": airport codes become the route
    const route = /\b([A-Z][A-Z0-9]{2,3})\s*(?:to|→|->|–|-)\s*([A-Z][A-Z0-9]{2,3})\b/.exec(title);
    if (route && (kind === 'flight' || kind === 'travel' || kind === 'other')) {
      item.from = route[1]!;
      item.to = route[2]!;
      if (kind === 'other') item.kind = 'flight';
    }
    out.push(item);
  }
  return out;
}

/** Shots from pasted text, one per line; "!" / "must" / "(must)" marks a must-have, "(if time)" a bonus. */
export function parsePastedShots(text: string): ShotPlan[] {
  const out: ShotPlan[] = [];
  for (const raw of text.split(/\r?\n/)) {
    let line = raw.trim().replace(/^\d{1,3}[.)]\s+/, '').replace(/^[•*\-–—]\s*/, '').replace(/^\[[ x]?\]\s*/i, '').trim();
    if (!line) continue;
    let priority: ShotPriority = 'want';
    if (/^!|\bmust(-have)?\b|\(must\)|\*must\*/i.test(line)) priority = 'must';
    else if (/\b(if time|bonus|nice to have)\b/i.test(line)) priority = 'bonus';
    line = line.replace(/^!+\s*/, '').replace(/\((must|must-have|if time|bonus|nice to have)\)/gi, '').replace(/\b(must-have|must)\s*[:—-]\s*/i, '').trim();
    if (!line) continue;
    const t = line.toLowerCase();
    const type: ShotType = /air.to.air|a2a|formation/.test(t) ? 'air_to_air' : /drone/.test(t) ? 'drone' : /cockpit|onboard|pov/.test(t) ? 'onboard' : /interview|sound ?bite/.test(t) ? 'interview' : /track|car mount|chase car/.test(t) ? 'tracking' : /flyby|fly-by|pass|takeoff|landing|ground.to.air/.test(t) ? 'ground_to_air' : /detail|b-?roll|cutaway/.test(t) ? 'broll' : 'other';
    out.push(newShot({ title: line.slice(0, 200), priority, type }));
  }
  return out;
}

/**
 * Running late (or early): moves every typed start from block `fromIndex` on by `minutes`.
 * Blocks that follow their predecessor move with it automatically.
 */
export function shiftDay(day: ScheduleDay, fromIndex: number, minutes: number): void {
  const resolved = resolveDay(day);
  for (const r of resolved) {
    if (r.index < fromIndex || r.item.start === null || r.startMin === null) continue;
    r.item.start = formatClock(r.startMin + minutes);
  }
  // a fixed-time first block that follows nothing still moves; an untimed one gets the new time
  const first = resolved[fromIndex];
  if (first && first.item.start === null && first.startMin !== null && fromIndex > 0 && !resolved.slice(fromIndex).some((r) => r.item.start !== null)) {
    first.item.start = formatClock(first.startMin + minutes);
  }
}

/** Days in date order; undated days keep their place at the end. */
export function sortDays(days: ScheduleDay[]): ScheduleDay[] {
  return [...days].map((d, i) => ({ d, i })).sort((a, b) => (a.d.date ?? '9999-99-99').localeCompare(b.d.date ?? '9999-99-99') || a.i - b.i).map((x) => x.d);
}
